// Utility layer (TS-2.5.04) - pure, no side effects. WCAG 2.1 relative-luminance / contrast-ratio
// math (https://www.w3.org/TR/WCAG21/#dfn-relative-luminance), used to pick a readable text colour
// for a background whose hex arrives as DATA - the admin-controlled vocabulary colours
// (BoardChip.colorHex, StatusView.colorHex - BRD section 7.2). The seed hexes are owner data the
// frontend must never edit; instead the frontend adapts: dark ink on light backgrounds, white on
// dark ones, computed per value at render time (D-247).
//
// The candidate inks are LITERALS on purpose, mirroring tokens.css's build-time defaults
// (--eg-color-ink #2b2b28, --eg-color-card #ffffff): contrast arithmetic needs concrete channel
// values, and a CSS var() cannot be read inside a pure function. If a runtime re-branding ever
// changes --eg-color-ink, this constant is the one place to follow it.
//
// Why the pure-black fallback below can never miss AA: for a background of luminance L, the better
// of white (ratio 1.05/(L+0.05)) and black (ratio (L+0.05)/0.05) is minimised where the two are
// equal, at L ~= 0.179, where both are ~4.58 - above the 4.5 normal-text threshold. So EVERY
// colour an admin can configure has an AA-passing pairing with black or white; "no readable ink
// exists" is mathematically unreachable and never needs a UI.

export const INK_HEX = "#2b2b28";
export const WHITE_HEX = "#ffffff";
export const BLACK_HEX = "#000000";

/** WCAG 2.1 AA minimum contrast for normal-size text. */
export const AA_NORMAL_TEXT = 4.5;
/** WCAG 2.1 AA minimum contrast for large text (>= 18.66px bold or 24px regular). */
export const AA_LARGE_TEXT = 3;

function parseHexChannels(hex: string): readonly [number, number, number] | null {
  const match = /^#?(?:([0-9a-f]{6})|([0-9a-f]{3}))$/i.exec(hex.trim());
  if (!match) return null;
  const six = match[1] ?? match[2]?.split("").map((c) => c + c).join("");
  if (!six) return null;
  const value = Number.parseInt(six, 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

function linearize(channel: number): number {
  const srgb = channel / 255;
  return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
}

/** WCAG 2.1 relative luminance of a hex colour (0 = black, 1 = white); null if unparseable. */
export function relativeLuminance(hex: string): number | null {
  const channels = parseHexChannels(hex);
  if (channels === null) return null;
  const [r, g, b] = channels;
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

function ratioOfLuminances(a: number, b: number): number {
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

/** WCAG 2.1 contrast ratio between two hex colours (1..21); null if either is unparseable. */
export function contrastRatio(hexA: string, hexB: string): number | null {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  if (a === null || b === null) return null;
  return ratioOfLuminances(a, b);
}

/**
 * The text colour to render on a data-supplied background: brand ink when ink reaches AA
 * (light backgrounds), else white when white reaches AA (dark backgrounds), else pure black -
 * which, per the header proof, always reaches AA when both branded inks miss (mid-tone
 * backgrounds like #EF4444, where ink is 3.77:1 and white 3.76:1 but black is 5.58:1).
 * An unparseable input keeps the pre-D-247 rendering (white) rather than throwing on admin data.
 */
export function readableTextColor(backgroundHex: string): string {
  const background = relativeLuminance(backgroundHex);
  if (background === null) return WHITE_HEX;

  const inkLuminance = relativeLuminance(INK_HEX);
  if (inkLuminance !== null && ratioOfLuminances(background, inkLuminance) >= AA_NORMAL_TEXT) {
    return INK_HEX;
  }
  if (ratioOfLuminances(background, 1) >= AA_NORMAL_TEXT) {
    return WHITE_HEX;
  }
  return BLACK_HEX;
}
